import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import notificationService from '../services/notificationService';
import cloudinaryService from '../services/cloudinaryService';
import themeService from '../services/themeService';
import { formatCurrency, formatDate, getStatusColor, getStatusText, getMonthName } from '../utils/helpers';
import { Provider, Request, Service, Conversation, MonthlyPayment } from '../models';
import {
  FiHome, FiInbox, FiList, FiTool, FiMessageSquare, FiUser, FiRefreshCw,
  FiEdit2, FiLogOut, FiHeadphones, FiBookOpen, FiShield, FiUpload, FiDollarSign,
  FiStar, FiCheck, FiX, FiPlus, FiTrash2, FiCamera, FiSettings, FiPhone,
  FiMapPin, FiMoon, FiSun, FiZap, FiMenu, FiBell, FiCreditCard, FiMail,
  FiDownload, FiShare2, FiLock, FiLink, FiFile, FiEye, FiPackage, FiAlertCircle, FiInfo, FiAlertTriangle, FiPrinter,
  FiSearch, FiShoppingCart
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { NotificationBadge, useUnreadRequests } from './NotificationBadge';
import QrScannerModal from './QrScannerModal';
import NotificationsScreen from './NotificationsScreen';
import QRCode from 'qrcode';

// ═══════════════════════════════════════════════════════════════
//  ServicesTab — مستقل خارج ProviderMainScreen لضمان re-render
// ═══════════════════════════════════════════════════════════════
const ServicesTab = ({
  services, isDigital, isStore,
  canEdit, canDelete,
  openAddServiceDialog, editService, deleteService,
  onShowQr,
}) => (
  <div className="p-4 md:p-5 max-w-4xl mx-auto">

    {/* Header */}
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold text-gray-900">
          {isDigital ? 'منتجاتي الرقمية' : isStore ? 'منتجاتي' : 'خدماتي'}
        </h2>
        {isDigital && (
          <span className="bg-gradient-to-r from-violet-600 to-blue-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
            رقمي
          </span>
        )}
        {services.length > 0 && (
          <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full">
            {services.length}
          </span>
        )}
      </div>
      <button
        onClick={openAddServiceDialog}
        className="bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-gray-700 transition shadow-sm"
      >
        <FiPlus />
        {isDigital ? 'رفع منتج' : isStore ? 'إضافة منتج' : 'إضافة خدمة'}
      </button>
    </div>

    {/* Empty State */}
    {services.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
          {isDigital
            ? <FiFile className="text-4xl text-gray-400" />
            : isStore
              ? <FiPackage className="text-4xl text-gray-400" />
              : <FiTool className="text-4xl text-gray-400" />
          }
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-1">
          {isDigital ? 'لا توجد منتجات رقمية بعد' : isStore ? 'لا توجد منتجات بعد' : 'لا توجد خدمات بعد'}
        </h3>
        <p className="text-sm text-gray-400 mb-6">
          {isDigital ? 'ارفع منتجك الأول وابدأ البيع' : 'اضغط الزر أعلاه للإضافة'}
        </p>
        <button
          onClick={openAddServiceDialog}
          className="bg-gradient-to-l from-gray-900 to-gray-700 text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:opacity-90 transition shadow"
        >
          {isDigital ? 'رفع منتج رقمي' : isStore ? 'إضافة منتج' : 'إضافة خدمة'}
        </button>
      </div>
    ) : (
      /* Grid */
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((service) => {
          const locked  = !canEdit(service.id);
          const cantDel = !canDelete(service.id);
          const hasFile = service.fileUrl?.length > 0;
          const hasPrev = service.previewUrl?.length > 0 || service.previewImages?.length > 0;

          return (
            <div key={service.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
              {/* Cover */}
              <div className="relative h-36 bg-gray-100">
                {service.imageUrl ? (
                  <img src={service.imageUrl} alt={service.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {isDigital
                      ? <FiFile className="text-4xl text-gray-300" />
                      : isStore
                        ? <FiPackage className="text-4xl text-gray-300" />
                        : <FiTool className="text-4xl text-gray-300" />
                    }
                  </div>
                )}

                {/* Digital type badge */}
                {isDigital && service.productType && (
                  <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 border
                    ${service.productType === 'pdf'
                      ? 'bg-violet-100 text-violet-700 border-violet-200'
                      : service.productType === 'zip'
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : 'bg-amber-100 text-amber-700 border-amber-200'
                    }`}>
                    {service.productType === 'pdf'
                      ? <FiFile size={9} />
                      : service.productType === 'zip'
                        ? <FiPackage size={9} />
                        : <FiLink size={9} />
                    }
                    {service.productType.toUpperCase()}
                  </div>
                )}

                {/* Lock badge */}
                {locked && (
                  <div className="absolute top-2 left-2 bg-gray-800/70 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
                    <FiLock size={9} /> مقيّدة
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="p-3.5">
                <h3 className="font-bold text-gray-900 text-sm truncate mb-0.5">{service.name}</h3>
                {service.description && (
                  <p className="text-xs text-gray-400 line-clamp-2 mb-2">{service.description}</p>
                )}

                {/* Price row */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-base font-extrabold text-gray-900">
                    {service.price?.toLocaleString('ar-DZ')} <span className="text-xs font-medium text-gray-500">د.ج</span>
                  </span>

                  {/* Stock badge (store) */}
                  {isStore && service.stock != null && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border
                      ${service.stock > 0
                        ? 'bg-green-50 text-green-700 border-green-100'
                        : 'bg-red-50 text-red-600 border-red-100'
                      }`}>
                      {service.stock > 0 ? `${service.stock} في المخزون` : 'نفد المخزون'}
                    </span>
                  )}
                </div>

                {/* Digital status badges */}
                {isDigital && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 border
                      ${hasFile
                        ? 'bg-violet-50 text-violet-700 border-violet-100'
                        : 'bg-gray-50 text-gray-400 border-gray-100'
                      }`}>
                      <FiFile size={9} />
                      {service.productType === 'link' ? 'رابط' : 'ملف رسمي'}
                      {hasFile ? ' ✓' : ' —'}
                    </span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 border
                      ${hasPrev
                        ? 'bg-blue-50 text-blue-600 border-blue-100'
                        : 'bg-gray-50 text-gray-400 border-gray-100'
                      }`}>
                      <FiEye size={9} />
                      معاينة {hasPrev ? ' ✓' : ' —'}
                    </span>
                  </div>
                )}

                {/* Category (store) */}
                {isStore && service.categoryName && (
                  <p className="text-[10px] text-gray-400 mb-3">
                    <span className="bg-gray-100 px-2 py-0.5 rounded">{service.categoryName}</span>
                  </p>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  {locked ? (
                    <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] text-gray-400 bg-gray-50 rounded-lg border border-gray-100">
                      <FiLock size={11} /> لا يمكن التعديل
                    </div>
                  ) : (
                    <button onClick={() => editService(service)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition">
                      <FiEdit2 size={13} /> تعديل
                    </button>
                  )}

                  <button
                    onClick={() => onShowQr(service)}
                    title="رمز QR للمنتج"
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition border border-gray-200"
                  >
                    <FiCamera size={13} />
                  </button>

                  <button
                    onClick={() => deleteService(service)}
                    title={cantDel ? 'يوجد طلب نشط — أكمل أو ألغِ الطلب أولاً' : 'حذف'}
                    className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition
                      ${cantDel
                        ? 'text-red-300 bg-red-50 cursor-not-allowed'
                        : 'text-red-600 bg-red-50 hover:bg-red-100'
                      }`}>
                    <FiTrash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════════
//  ProviderMainScreen
// ═══════════════════════════════════════════════════════════════
const ProviderMainScreen = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [provider, setProvider] = useState(null);
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [currentMonthPayment, setCurrentMonthPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab ? parseInt(tab) : 0;
  });
  const [requestSubTab, setRequestSubTab] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [confirmationRequests, setConfirmationRequests] = useState([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedConfirmation, setSelectedConfirmation] = useState(null);
  const [showProCard, setShowProCard] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(themeService.isDarkMode());
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const [selectedQrService, setSelectedQrService] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [updatingPaymentMethods, setUpdatingPaymentMethods] = useState(false);

  // States for Complaint Modal
  const [complaintModalOpen, setComplaintModalOpen] = useState(false);
  const [complaintRequest, setComplaintRequest] = useState(null);
  const [complaintReason, setComplaintReason] = useState('');
  const [submittingComplaint, setSubmittingComplaint] = useState(false);

  // ─── Service Form State ────────────────────────────────────────
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    stock: '',
    categoryName: '',
    productType: 'pdf',
    fileUrl: '',
    previewUrl: '',
    linkUrl: '',
    mediaUrls: [],
    allowDownload: false,
  });
  const [serviceImageFile, setServiceImageFile] = useState(null);
  const [serviceImagePreview, setServiceImagePreview] = useState(null);
  const [serviceFile, setServiceFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [serviceMediaFiles, setServiceMediaFiles] = useState([]); // [{ file, previewUrl }]
  const [uploadingServiceImage, setUploadingServiceImage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [serviceStatuses, setServiceStatuses] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  const requestChannelRef = useRef(null);
  const { unreadCount, markAllAsRead } = useUnreadRequests(requests, 'provider');
  const cardRef = useRef(null);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  // 🆕 جلب عدد الإشعارات غير المقروءة
  useEffect(() => {
    const fetchUnreadNotifications = async () => {
      if (!user?.id) return;
      try {
        const count = await supabaseService.getUnreadNotificationsCount(user.id);
        setUnreadNotificationsCount(count);
      } catch (e) {
        console.error('Error fetching unread notifications count:', e);
      }
    };
    fetchUnreadNotifications();
    const interval = setInterval(fetchUnreadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // ─── Account type helpers ──────────────────────────────────────
  // ✅ إصلاح: useMemo لضمان تحديث القيم عند تحميل provider
  const isDigital = React.useMemo(() => provider?.isDigitalProducts ?? false, [provider]);
  const isStore   = React.useMemo(() => provider?.isStore ?? false, [provider]);

  useEffect(() => {
    setSearchParams({ tab: activeTab.toString() }, { replace: true });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    loadProviderData();
    const removeListener = themeService.addListener((theme) => {
      setIsDarkMode(theme === 'dark');
    });
    return () => {
      if (requestChannelRef.current) {
        supabaseService.client.removeChannel(requestChannelRef.current);
      }
      if (walletChannelRef.current) {
        supabaseService.client.removeChannel(walletChannelRef.current);
      }
      removeListener();
    };
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        setNotificationPermission(permission);
        if (permission === 'granted') toast.success('تم تفعيل الإشعارات بنجاح');
        else if (permission === 'denied') toast.error('تم رفض الإشعارات. يمكنك تفعيلها يدويًا من إعدادات المتصفح.');
      });
    }
  }, []);

  useEffect(() => {
    if (activeTab === 1) markAllAsRead();
  }, [activeTab, markAllAsRead]);

  useEffect(() => {
    let intervalId;
    if (activeTab === 0) {
      intervalId = setInterval(() => { loadProviderData(); }, 200);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [activeTab]);

  const openComplaintDialog = (request) => {
    setComplaintRequest(request);
    setComplaintReason('');
    setComplaintModalOpen(true);
  };

  const handleSendComplaint = async () => {
    if (!complaintReason.trim()) {
      toast.error('الرجاء إدخال سبب الشكوى');
      return;
    }
    setSubmittingComplaint(true);
    try {
      await supabaseService.createComplaint({
        clientId: complaintRequest.clientId,
        providerId: complaintRequest.providerId,
        requestId: complaintRequest.id,
        reason: complaintReason.trim()
      });
      toast.success('تم تقديم الشكوى بنجاح، سنقوم بمراجعتها قريباً');
      setComplaintModalOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('فشل تقديم الشكوى، يرجى المحاولة لاحقاً');
    } finally {
      setSubmittingComplaint(false);
    }
  };

  const handleUnconfirmedServiceReport = async (request) => {
    if (!window.confirm('هل أنت متأكد من تسجيل بلاغ "الزبون لم يؤكد خدمته"؟')) return;
    try {
      const { error } = await supabaseService.client
        .from('unconfirmed_services')
        .insert({
          request_id: request.id,
          service_id: request.serviceId || request.service?.id,
          provider_id: provider?.id || request.providerId,
          client_id: request.clientId || request.client?.id,
          created_at: new Date().toISOString()
        });
      if (error) throw error;
      toast.success('تم تسجيل بلاغ "الزبون لم يؤكد خدمته" بنجاح في قاعدة البيانات');
    } catch (error) {
      console.error('Error reporting unconfirmed service:', error);
      toast.error('فشل تسجيل البلاغ: ' + (error.message || 'خطأ غير معروف'));
    }
  };

  // ─── Load provider data ────────────────────────────────────────
  const loadProviderData = async () => {
    try {
      const userId = user.id;
      const [providerData, requestsData, servicesData, currentMonthPaymentData] = await Promise.all([
        supabaseService.getProviderById(userId),
        supabaseService.getRequestsForUser(userId, { isClient: false }),
        supabaseService.getServicesForProvider(userId),
        supabaseService.getCurrentMonthPayment(userId),
      ]);
      setProvider(providerData);
      setRequests(requestsData || []);
      setServices(servicesData || []);
      setCurrentMonthPayment(currentMonthPaymentData);
      const pendingConfirmations = (requestsData || []).filter(r => r.status === 'pending_provider_confirmation');
      setConfirmationRequests(pendingConfirmations);
      setupRealtimeListeners(userId);
      loadServiceStatuses(servicesData || []);
    } catch (error) {
      console.error('Error loading provider data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Service statuses (edit/delete guards) ─────────────────────
  const loadServiceStatuses = async (svcs) => {
    if (!svcs?.length) return;
    setLoadingStatuses(true);
    try {
      const entries = await Promise.all(
        svcs.map(async (s) => {
          const [hasActiveReq, hasAnyReq, hasPurchase] = await Promise.all([
            supabaseService.hasActiveRequests(s.id).catch(() => false),
            supabaseService.hasAnyRequests(s.id).catch(() => false),
            supabaseService.hasDigitalPurchases(s.id).catch(() => false),
          ]);
          return [s.id, { hasActiveReq, hasAnyReq, hasPurchase }];
        })
      );
      setServiceStatuses(Object.fromEntries(entries));
    } catch (e) {
      console.error('loadServiceStatuses:', e);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const canEdit = (id) => {
    // السماح بالتعديل دائماً، وسيتم قفل حقل السعر فقط إذا كان هناك طلبات أو مبيعات مسجلة
    return true;
  };

  const canDelete = (id) => {
    const s = serviceStatuses[id];
    if (!s) return true;
    return !s.hasActiveReq;
  };

  // ─── Reset service form ────────────────────────────────────────
  const resetServiceForm = () => {
    setEditingService(null);
    setServiceFormData({
      name: '', description: '', price: '', imageUrl: '',
      stock: '', categoryName: '',
      productType: 'pdf', fileUrl: '', previewUrl: '', linkUrl: '', mediaUrls: [],
      allowDownload: false,
    });
    setServiceImageFile(null);
    setServiceImagePreview(null);
    setServiceFile(null);
    setPreviewFile(null);
    setServiceMediaFiles([]);
  };

  const openAddServiceDialog = () => {
    resetServiceForm();
    setShowServiceDialog(true);
  };

  const editService = (service) => {
    setEditingService(service);
    setServiceFormData({
      name:         service.name         || '',
      description:  service.description  || '',
      price:        service.price?.toString() || '',
      imageUrl:     service.imageUrl     || '',
      stock:        service.stock?.toString()    || '',
      categoryName: service.categoryName || '',
      productType:  service.productType  || 'pdf',
      fileUrl:      service.fileUrl      || '',
      previewUrl:   service.previewUrl   || '',
      linkUrl:      service.linkUrl      || '',
      mediaUrls:    service.mediaUrls    || service.media_urls || [],
      allowDownload: service.allowDownload ?? service.allow_download ?? true,
    });
    setServiceImagePreview(service.imageUrl || null);
    setServiceImageFile(null);
    setServiceFile(null);
    setPreviewFile(null);
    setServiceMediaFiles([]);
    setShowServiceDialog(true);
  };

  // ─── Save service ──────────────────────────────────────────────
  const saveService = async () => {
    const { name, description, price, imageUrl, stock, categoryName } = serviceFormData;

    if (!name || !description || !price) {
      toast.error('يرجى ملء الحقول الأساسية (الاسم، الوصف، السعر)');
      return;
    }
    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }

    // ✅ إصلاح: validation منفصل لكل نوع رقمي
    if (isDigital) {
      if (serviceFormData.productType === 'link' && !serviceFormData.linkUrl.trim()) {
        toast.error('يرجى إدخال رابط المنتج');
        return;
      }
      if (serviceFormData.productType !== 'link' && !serviceFile && !serviceFormData.fileUrl) {
        toast.error('يرجى رفع الملف الرقمي');
        return;
      }
    }

    try {
      let finalImageUrl = imageUrl;
      if (serviceImageFile) {
        setUploadingServiceImage(true);
        try {
          finalImageUrl = await cloudinaryService.uploadImage(serviceImageFile, 'services');
        } catch {
          toast.error('فشل رفع صورة الغلاف');
          return;
        } finally {
          setUploadingServiceImage(false);
        }
      }

      let finalFileUrl = serviceFormData.fileUrl;
      if (serviceFile) {
        setUploadingFile(true);
        try {
          finalFileUrl = await cloudinaryService.uploadFile(serviceFile, 'digital');
        } catch {
          toast.error('فشل رفع الملف الرقمي');
          return;
        } finally {
          setUploadingFile(false);
        }
      }

      let finalPreviewUrl = serviceFormData.previewUrl;
      if (previewFile) {
        setUploadingPreview(true);
        try {
          finalPreviewUrl = await cloudinaryService.uploadFile(previewFile, 'previews');
        } catch {
          toast.error('فشل رفع ملف المعاينة');
          return;
        } finally {
          setUploadingPreview(false);
        }
      }

      // ── Upload media gallery files (images + videos) ──────────
      let finalMediaUrls = [...(serviceFormData.mediaUrls || [])];
      if (serviceMediaFiles.length > 0) {
        setUploadingMedia(true);
        try {
          for (const item of serviceMediaFiles) {
            const isVideo = item.file.type.startsWith('video/');
            const uploadedUrl = isVideo
              ? await cloudinaryService.uploadFile(item.file, 'services')
              : await cloudinaryService.uploadImage(item.file, 'services');
            if (uploadedUrl) finalMediaUrls.push(uploadedUrl);
          }
        } catch {
          toast.error('فشل رفع بعض ملفات الوسائط');
          return;
        } finally {
          setUploadingMedia(false);
        }
      }

      // Ensure cover image is first in mediaUrls if present
      if (finalImageUrl && !finalMediaUrls.includes(finalImageUrl)) {
        finalMediaUrls = [finalImageUrl, ...finalMediaUrls];
      }

      let categoryId = null;
      if (isStore && categoryName) {
        categoryId = await supabaseService.getOrCreateCategoryByName(categoryName);
      }

      // ✅ إصلاح: payload يقرأ من serviceFormData مباشرة
      const payload = {
        name,
        description,
        price: priceNum,
        imageUrl: finalImageUrl,
        mediaUrls: finalMediaUrls,
        ...(isStore && {
          stock:        stock !== '' ? parseInt(stock) : null,
          categoryName: categoryName || null,
        }),
        ...(isDigital && {
          productType: serviceFormData.productType,
          fileUrl:     serviceFormData.productType === 'link'
                         ? serviceFormData.linkUrl
                         : finalFileUrl,
          previewUrl:  finalPreviewUrl,
          allowDownload: serviceFormData.allowDownload,
        }),
      };

      if (editingService) {
        await supabaseService.updateService(editingService.id, { ...payload, categoryId });
        toast.success('تم تحديث الخدمة بنجاح ✓');
        setServices(prev => prev.map(s => s.id === editingService.id ? { ...s, ...payload, categoryId } : s));
      } else {
        // ✅ إصلاح: تمرير كل حقل بشكل صريح لـ addService
        const saved = await supabaseService.addService({
          providerId:  provider.id,
          name:        payload.name,
          description: payload.description,
          price:       payload.price,
          imageUrl:    payload.imageUrl    ?? null,
          stock:       payload.stock       ?? null,
          categoryId:  categoryId,
          fileUrl:     payload.fileUrl     ?? null,
          previewUrl:  payload.previewUrl  ?? null,
          productType: payload.productType ?? null,
          mediaUrls:   payload.mediaUrls   ?? null,
          allowDownload: payload.allowDownload ?? false,
        });
        toast.success(isDigital ? 'تمت إضافة المنتج الرقمي ✓' : 'تمت إضافة الخدمة ✓');
        setServices(prev => [...prev, { ...saved, ...payload, categoryId }]);
      }

      setShowServiceDialog(false);
      resetServiceForm();
      loadServiceStatuses(services);
    } catch (e) {
      console.error('saveService:', e);
      toast.error('فشل حفظ الخدمة');
    }
  };

  // ─── Delete service ────────────────────────────────────────────
  const deleteService = async (service) => {
    const id = service?.id;
    if (!id) { toast.error('معرّف الخدمة غير موجود'); return; }

    if (!canDelete(id)) {
      toast.error('لا يمكن الحذف — يوجد طلب نشط مرتبط بهذه الخدمة');
      return;
    }
    if (!window.confirm('هل أنت متأكد من حذف هذه الخدمة؟ لا يمكن التراجع.')) return;

    try {
      await supabaseService.deleteService(id);
      toast.success('تم حذف الخدمة');
      setServices(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      console.error('deleteService:', e);
      toast.error('فشل حذف الخدمة');
    }
  };

  const toggleDarkMode = () => {
    const newTheme = themeService.toggleTheme();
    setIsDarkMode(newTheme === 'dark');
    toast.success(`تم تفعيل الوضع ${newTheme === 'dark' ? 'المظلم' : 'الفاتح'}`);
  };

  const navigateToGame = () => { navigate('/memory-game'); };

  const walletChannelRef = useRef(null);

  const setupRealtimeListeners = (userId) => {
    // استماع لتغييرات المحفظة (استلام رصيد)
    if (walletChannelRef.current) {
      supabaseService.client.removeChannel(walletChannelRef.current);
    }
    walletChannelRef.current = supabaseService.listenToWalletChanges(userId, (transaction) => {
      if (transaction && transaction.type === 'transfer_in') {
        const amount = transaction.amount || 0;
        toast.success(`💰 تم استلام ${formatCurrency(amount)} في محفظتك!`, {
          duration: 5000,
          position: 'top-center',
        });
        // تحديث بيانات المزود لعكس الرصيد الجديد
        loadProviderData();
      }
    });
  };

  const updateAvailability = async (isAvailable) => {
    try {
      await supabaseService.updateProvider(provider.id, { available: isAvailable });
      loadProviderData();
      toast.success(`تم تحديث حالة التوفر إلى ${isAvailable ? 'متاح' : 'غير متاح'}`);
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('فشل تحديث حالة التوفر');
    }
  };

  const updatePaymentMethods = async (method) => {
    if (!provider || updatingPaymentMethods) return;
    setUpdatingPaymentMethods(true);
    try {
      let acceptsWallet = false;
      let acceptsCash = false;
      if (method === 'wallet') {
        acceptsWallet = true;
      } else if (method === 'cash') {
        acceptsCash = true;
      } else if (method === 'both') {
        acceptsWallet = true;
        acceptsCash = true;
      }
      await supabaseService.updateProvider(provider.id, {
        accepts_wallet: acceptsWallet,
        accepts_cash: acceptsCash,
      });
      setProvider(prev => ({ ...prev, acceptsWallet, acceptsCash }));
      toast.success('تم حفظ طريقة الدفع بنجاح');
    } catch (error) {
      console.error('Error updating payment methods:', error);
      toast.error('فشل تحديث طريقة الدفع');
    } finally {
      setUpdatingPaymentMethods(false);
    }
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      await supabaseService.updateRequestStatus(requestId, status);
      if (status === 'accepted') {
        const request = requests.find(r => r.id === requestId);
        await notificationService.notifyRequestAccepted(request.clientId, request);
        toast.success('تم قبول الطلب');
      } else if (status === 'rejected') {
        toast.success('تم رفض الطلب');
      }
      setRequests(prev => prev.map(req => req.id === requestId ? { ...req, status } : req));
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('فشل تحديث حالة الطلب');
    }
  };

  const navigateToChat = async (request) => {
    if (!request.clientId) {
      toast.error('خطأ: معرف العميل غير متوفر');
      return;
    }

    toast.loading('جاري إنشاء المحادثة...', { id: 'chat-create' });

    try {
      const convId = await supabaseService.getOrCreateConversation(request.clientId, user.id);
      if (convId) {
        toast.success('تم إنشاء المحادثة', { id: 'chat-create' });
        navigate(`/chat/${convId}`, {
          state: {
            otherUserName: request.client?.fullName || 'عميل',
            otherUserAvatarUrl: request.client?.avatarUrl
          }
        });
      } else {
        toast.error('فشل إنشاء المحادثة', { id: 'chat-create' });
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast.error('حدث خطأ أثناء فتح المحادثة', { id: 'chat-create' });
    }
  };

  const handleConfirmation = async (requestId, confirmed) => {
    try {
      await supabaseService.confirmRequestCompletion(requestId, confirmed);
      if (confirmed) toast.success('تم تأكيد إتمام الخدمة بنجاح');
      else toast.success('تم رفض طلب الإتمام');
      setShowConfirmationDialog(false);
      setSelectedConfirmation(null);
      loadProviderData();
    } catch (error) {
      console.error('Error handling confirmation:', error);
      toast.error('فشل معالجة الطلب');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('فشل تسجيل الخروج');
    }
  };

  const handleSupportClick = async () => {
    const supportClientId = "e9260ad4-663f-4f35-83ed-684aaf7ba34b";
    const supportName = "الدعم الفني";
    const supportAvatar = "https://mute-glitter-cfe7.mouhoubifares12.workers.dev/file/4_z9e3b76d808a9dbfd92cd0415_f118e6e3159ea5875_d20260718_m154753_c005_v0501045_t0019_u01784389673991?t=khidma_secret_2026_xyz";
    
    toast.loading('جاري الاتصال بالدعم...', { id: 'support-chat' });
    try {
      const conversationId = await supabaseService.getOrCreateConversation(supportClientId, user.id);
      if (conversationId) {
        toast.success('تم فتح محادثة الدعم', { id: 'support-chat' });
        navigate(`/chat/${conversationId}`, {
          state: {
            otherUserName: supportName,
            otherUserAvatarUrl: supportAvatar
          }
        });
      } else {
        toast.error('فشل إنشاء محادثة الدعم', { id: 'support-chat' });
      }
    } catch (error) {
      console.error("Error opening support chat:", error);
      toast.error('حدث خطأ أثناء فتح المحادثة', { id: 'support-chat' });
    }
  };

  const getFormattedRating = (rating) => {
    if (rating === undefined || rating === null || isNaN(rating)) return '0.0';
    return parseFloat(rating).toFixed(1);
  };

  const renderMonthlyPayment = () => {
    if (!currentMonthPayment) {
      return (
        <div className="bg-white md:bg-gray-50 p-4 md:p-5 shadow-sm border-b md:border border-gray-200 md:rounded-xl">
          <div className="bg-gray-100 rounded-full p-2 mb-2 inline-block">
            <FiDollarSign className="text-gray-400 text-xl" />
          </div>
          <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1">ملخص مالي الشهر الحالي</h3>
          <p className="text-xs md:text-sm text-gray-500">لا توجد بيانات مالية مسجلة لهذا الشهر بعد.</p>
        </div>
      );
    }
    try {
      const month = currentMonthPayment.month;
      const totalEarnings = currentMonthPayment.totalEarnings || currentMonthPayment.total_earnings || 0;
      const commission    = currentMonthPayment.commission || 0;
      const netEarnings   = currentMonthPayment.netEarnings || currentMonthPayment.net_earnings || 0;
      const paymentStatus = currentMonthPayment.payment_status || currentMonthPayment.paymentStatus;
      const monthNumber   = month ? (new Date(month)).getMonth() + 1 : new Date().getMonth() + 1;
      const monthName     = getMonthName(monthNumber);
      const year          = month ? (new Date(month)).getFullYear() : new Date().getFullYear();
      const isPaid        = paymentStatus === 'paid';
      return (
        <div className="bg-white md:bg-gray-50 p-4 md:p-5 shadow-sm border-b md:border border-gray-200 md:rounded-xl">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base md:text-lg font-bold text-gray-900">ملخص مالي لشهر {monthName} {year}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] md:text-xs font-bold ${isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {isPaid ? 'تم الدفع' : 'معلق'}
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-gray-600 text-sm md:text-base">
              <span>إجمالي الأرباح</span>
              <span className="font-medium text-gray-900">{formatCurrency(totalEarnings)}</span>
            </div>
            <div className="flex justify-between text-gray-600 text-sm md:text-base">
              <span>العمولة (10%)</span>
              <span className="font-medium text-red-500">-{formatCurrency(commission)}</span>
            </div>
            <div className="flex justify-between font-bold text-sm md:text-base pt-2 border-t border-gray-200">
              <span>صافي الأرباح</span>
              <span className="text-gray-900">{formatCurrency(netEarnings)}</span>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      return (
        <div className="bg-red-50 p-4 md:p-5 shadow-sm border-b md:border border-gray-200 md:rounded-xl">
          <p className="text-red-600 text-xs md:text-sm">حدث خطأ في عرض البيانات المالية</p>
        </div>
      );
    }
  };

  const ConfirmationCard = ({ request }) => (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4 shadow-lg">
      <div className="flex items-center space-x-3 space-x-reverse mb-3">
        <div className="bg-blue-600 rounded-full p-2"><FiCheck className="text-white text-lg" /></div>
        <div>
          <h3 className="font-bold text-blue-900">طلب تأكيد إتمام خدمة</h3>
          <p className="text-sm text-blue-700">يحتاج إلى مراجعتك</p>
        </div>
      </div>
      <div className="bg-white rounded-lg p-3 mb-3">
        <div className="flex items-center space-x-3 space-x-reverse mb-2">
          <img src={request.client?.avatarUrl || 'https://via.placeholder.com/150'} alt="Client"
            className="w-12 h-12 rounded-full border-2 border-blue-300" />
          <div>
            <p className="font-medium text-gray-900">{request.client?.fullName || 'عميل'}</p>
            <p className="text-sm text-gray-600">
              رقم الهاتف: {request.client?.phone ? (
                <a href={`tel:${request.client.phone}`} className="text-blue-600 font-bold underline hover:text-blue-800 dir-ltr inline-block">
                  {request.client.phone}
                </a>
              ) : 'غير متوفر'}
            </p>
          </div>
        </div>
        <div className="border-t pt-2 mt-2">
          <p className="text-sm text-gray-700 mb-1"><span className="font-medium">الخدمة:</span> {request.service?.name || 'خدمة'}</p>
          <p className="text-sm text-gray-700 mb-1"><span className="font-medium">السعر:</span> {formatCurrency(request.service?.price || 0)}</p>
          {request.rating && (
            <div className="flex items-center space-x-2 space-x-reverse mt-2">
              <FiStar className="text-yellow-500" />
              <span className="text-sm font-medium">{request.rating}/5</span>
            </div>
          )}
        </div>
      </div>
      <p className="text-sm text-blue-800 mb-3 font-medium">هل تم إتمام هذه الخدمة فعلياً؟</p>
      <div className="flex space-x-3 space-x-reverse">
        <button onClick={() => { setSelectedConfirmation(request); setShowConfirmationDialog(true); }}
          className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 space-x-reverse">
          <FiCheck className="text-lg" /><span>نعم، تمت الخدمة</span>
        </button>
        <button onClick={() => handleConfirmation(request.id, false)}
          className="flex-1 bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 space-x-reverse">
          <FiX className="text-lg" /><span>لا، لم تتم</span>
        </button>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  //  TABS
  // ════════════════════════════════════════════════════════════════
  const tabs = [
    // ── Tab 0: Home ──────────────────────────────────────────────
    {
      id: 0,
      name: 'الرئيسية',
      icon: FiHome,
      content: (
        <div className="h-full flex flex-col bg-gray-50 overflow-hidden font-sans">
          {/* HEADER */}
          <div className="flex-shrink-0 bg-gradient-to-l from-gray-900 to-gray-800 text-white shadow-md z-20">
            <div className="p-4 md:px-6 md:py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="relative">
                  <img src={provider?.avatarUrl || 'https://via.placeholder.com/150'} alt="Profile"
                    className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-white/50 shadow-sm" />
                  {provider?.isVerified && (
                    <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-0.5 border-2 border-gray-900">
                      <FiCheck className="text-white text-xs" />
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-base md:text-lg font-bold leading-tight">مرحباً، {provider?.fullName?.split(' ')[0] || 'المزود'}</h2>
                  <p className="text-xs md:text-sm text-gray-300 font-medium">لوحة التحكم الرئيسية</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                {/* ✅ زر الشحن + الرصيد */}
                <button onClick={() => navigate('/wallet')} className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 transition active:scale-95 flex items-center gap-1.5" title="شحن المحفظة">
                  <FiDollarSign className="text-emerald-300 w-4 h-4" />
                  <span className="text-[11px] font-bold text-emerald-300 hidden lg:inline">
                    {provider?.wallet?.toLocaleString('ar-DZ') || '0'} د.ج
                  </span>
                  <span className="text-[11px] font-bold text-emerald-300 hidden lg:inline">شحن</span>
                </button>
                <button onClick={() => navigate('/personal-qr')} className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 transition active:scale-95 flex items-center gap-1.5" title="رمز المحفظة">
                  <FiDollarSign className="text-emerald-300 w-4 h-4" />
                  <span className="text-[11px] font-bold text-emerald-300 hidden lg:inline">الكود</span>
                </button>
                <button onClick={() => setQrScannerOpen(true)} className="px-3 py-2 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 transition active:scale-95 flex items-center gap-1.5" title="مسح رمز QR">
                  <FiCamera className="text-blue-300 w-4 h-4" />
                  <span className="text-[11px] font-bold text-blue-300 hidden lg:inline">مسح</span>
                </button>
                <button onClick={loadProviderData} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition active:scale-95" title="تحديث البيانات">
                  <FiRefreshCw className="text-white w-5 h-5" />
                </button>
                <button onClick={toggleDarkMode} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition active:scale-95">
                  {isDarkMode ? <FiSun className="text-yellow-300 w-5 h-5" /> : <FiMoon className="text-gray-300 w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* MOBILE */}
          <div className="md:hidden flex-1 flex flex-col p-3 gap-3 overflow-hidden">
            {notificationPermission !== 'granted' && (
              <div className="bg-blue-600 text-white p-3 rounded-xl shadow-lg flex items-center justify-between gap-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="bg-white/20 p-1.5 rounded-full"><FiBell className="text-sm" /></div>
                  <span className="text-xs font-bold">تفعيل الإشعارات للتواصل السريع</span>
                </div>
                <button onClick={() => Notification.requestPermission().then(p => { setNotificationPermission(p); p === 'granted' ? toast.success('تم التفعيل') : toast.error('تم رفض الإشعارات'); })}
                  className="bg-white text-blue-600 text-xs px-3 py-1.5 rounded-lg font-bold shadow-sm hover:bg-gray-100 transition">
                  تفعيل الآن
                </button>
              </div>
            )}
            <div className="flex gap-2 flex-shrink-0">
              <div className="flex-1 bg-white p-2.5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center items-center text-center">
                <h3 className="text-[10px] font-bold text-gray-400 mb-1.5">حالتك</h3>
                <button onClick={() => updateAvailability(!provider?.available)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors mb-1.5 ${provider?.available ? 'bg-green-500' : 'bg-red-500'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${provider?.available ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
                <p className={`text-[10px] font-bold ${provider?.available ? 'text-green-600' : 'text-red-600'}`}>
                  {provider?.available ? 'متاح' : 'مشغول'}
                </p>
              </div>
              <div className="flex-1 bg-white p-2.5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center items-center text-center">
                <div className="bg-yellow-50 p-1.5 rounded-full mb-1"><FiStar className="text-yellow-500 text-lg" /></div>
                <span className="text-base font-bold text-gray-800">{getFormattedRating(provider?.averageRating)}</span>
              </div>
              <button onClick={navigateToGame} className="flex-1 bg-purple-50 p-2.5 rounded-xl border border-purple-100 flex flex-col justify-center items-center text-center active:bg-purple-100 transition">
                <div className="bg-purple-100 p-1.5 rounded-full mb-1"><FiZap className="text-purple-600 text-lg" /></div>
                <span className="text-[10px] font-bold text-purple-800">لعبة</span>
              </button>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex-shrink-0">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1">
                  <span className="bg-green-100 text-green-600 p-1 rounded"><FiDollarSign className="text-xs" /></span>
                  الملخص المالي
                </h3>
                {currentMonthPayment && (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${currentMonthPayment.payment_status === 'paid' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-orange-50 text-orange-700 border-orange-100'}`}>
                    {currentMonthPayment.payment_status === 'paid' ? 'تم الدفع' : 'معلق'}
                  </span>
                )}
              </div>
              {currentMonthPayment ? (
                <div>
                  <div className="mb-3">
                    <p className="text-[10px] text-gray-400 font-medium">صافي الأرباح</p>
                    <p className="text-xl font-extrabold text-gray-900 tracking-tight">{formatCurrency(currentMonthPayment.netEarnings || currentMonthPayment.net_earnings || 0)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-gray-500">الإجمالي</p>
                      <p className="text-sm font-bold text-gray-700">{formatCurrency(currentMonthPayment.totalEarnings || currentMonthPayment.total_earnings || 0)}</p>
                    </div>
                    <div className="text-left border-l border-gray-200 pl-2">
                      <p className="text-[10px] text-gray-500">العمولة</p>
                      <p className="text-sm font-bold text-red-500">-{formatCurrency(currentMonthPayment.commission || 0)}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-2 text-gray-400 text-xs">لا توجد حركات مالية هذا الشهر</div>
              )}
            </div>

            {/* إعدادات طرق الدفع - موبايل */}
            <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex-shrink-0">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1">
                  <span className="bg-blue-100 text-blue-600 p-1 rounded"><FiCreditCard className="text-xs" /></span>
                  طرق الدفع
                </h3>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  onClick={() => updatePaymentMethods('cash')}
                  disabled={updatingPaymentMethods}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-bold transition-all ${
                    provider?.acceptsCash && !provider?.acceptsWallet
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span>💵</span>
                  <span>كاش</span>
                </button>
                <button
                  onClick={() => updatePaymentMethods('wallet')}
                  disabled={updatingPaymentMethods}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-bold transition-all ${
                    provider?.acceptsWallet && !provider?.acceptsCash
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span>👛</span>
                  <span>محفظة</span>
                </button>
                <button
                  onClick={() => updatePaymentMethods('both')}
                  disabled={updatingPaymentMethods}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-bold transition-all ${
                    provider?.acceptsWallet && provider?.acceptsCash
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span>💵👛</span>
                  <span>الكل</span>
                </button>
              </div>
              <p className="text-[9px] text-gray-400 mt-1.5 text-center">اختر طريقة دفع واحدة أو كليهما</p>
            </div>

            {confirmationRequests.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl flex-shrink-0 flex flex-col gap-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-100 text-blue-600 p-1.5 rounded-lg"><FiCheck className="text-sm" /></div>
                    <div>
                      <h3 className="text-xs font-bold text-blue-900">تأكيد إنجاز الخدمة</h3>
                      <p className="text-[10px] text-blue-800">{confirmationRequests[0].client?.fullName}</p>
                    </div>
                  </div>
                  <span className="bg-blue-200 text-blue-800 text-[9px] font-bold px-1.5 py-0.5 rounded">{confirmationRequests.length}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setSelectedConfirmation(confirmationRequests[0]); setShowConfirmationDialog(true); }} className="flex-1 bg-blue-600 text-white py-1.5 rounded-lg text-xs font-bold shadow-sm hover:bg-blue-700 transition">نعم، تمت</button>
                  <button onClick={() => handleConfirmation(confirmationRequests[0].id, false)} className="flex-1 bg-white text-blue-600 border border-blue-200 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-50 transition">لا، لم تتم</button>
                </div>
              </div>
            )}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-0">
              <div className="p-3 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-xl">
                <h3 className="text-sm font-bold text-gray-800">الطلبات الجديدة</h3>
                <span className="bg-gray-900 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{requests.filter(r => r.status === 'pending').length}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2 scrollbar-thin">
                {requests.filter(r => r.status === 'pending').length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-60 py-6">
                    <FiInbox className="text-3xl mb-2" /><p className="text-xs font-bold">لا توجد طلبات حالياً</p>
                  </div>
                ) : requests.filter(r => r.status === 'pending').map((request) => (
                  <div key={request.id} className="bg-white border border-gray-100 p-3 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={request.client?.avatarUrl || 'https://via.placeholder.com/150'} alt="Client" className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{request.client?.fullName}</p>
                        <p className="text-[11px] text-gray-500 truncate font-medium">{request.service?.name}</p>
                        <div className="mt-1">
                          {request.paymentMethod === 'wallet' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
                              💰 محفظة
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                              💵 كاش
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => updateRequestStatus(request.id, 'accepted')} className="flex-1 bg-green-600 text-white py-2 rounded-lg text-xs font-bold hover:bg-green-700 transition shadow-sm shadow-green-200">قبول</button>
                      <button onClick={() => updateRequestStatus(request.id, 'rejected')} className="flex-1 bg-red-50 text-red-600 py-2 rounded-lg text-xs font-bold hover:bg-red-100 transition border border-red-100">رفض</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DESKTOP */}
          <div className="hidden md:flex flex-1 gap-4 p-4 overflow-hidden">
            <div className="w-80 flex flex-col gap-4 flex-shrink-0">
              {/* روابط سريعة - تظهر مباشرة بدون سكرول */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-2 gap-px bg-gray-100">
                  <Link to="/providers" className="flex items-center gap-2 p-3 bg-white hover:bg-gray-50 transition group text-right">
                    <div className="bg-gray-900 text-white p-2 rounded-lg group-hover:scale-105 transition-transform flex-shrink-0">
                      <FiSearch className="text-sm" />
                    </div>
                    <span className="text-xs font-bold text-gray-700">ابحث عن مزود</span>
                  </Link>
                  <Link to="/store" className="flex items-center gap-2 p-3 bg-white hover:bg-orange-50 transition group text-right">
                    <div className="bg-orange-100 text-orange-600 p-2 rounded-lg group-hover:scale-105 transition-transform flex-shrink-0">
                      <FiShoppingCart className="text-sm" />
                    </div>
                    <span className="text-xs font-bold text-gray-700">المتجر</span>
                  </Link>
                  <button onClick={navigateToGame} className="flex items-center gap-2 p-3 bg-white hover:bg-purple-50 transition group text-right">
                    <div className="bg-purple-100 text-purple-600 p-2 rounded-lg group-hover:scale-105 transition-transform flex-shrink-0">
                      <FiZap className="text-sm" />
                    </div>
                    <span className="text-xs font-bold text-gray-700">الألعاب</span>
                  </button>
                  <button onClick={() => setQrScannerOpen(true)} className="flex items-center gap-2 p-3 bg-white hover:bg-blue-50 transition group text-right">
                    <div className="bg-blue-100 text-blue-600 p-2 rounded-lg group-hover:scale-105 transition-transform flex-shrink-0">
                      <FiCamera className="text-sm" />
                    </div>
                    <span className="text-xs font-bold text-gray-700">مسح QR</span>
                  </button>
                </div>
              </div>
              {notificationPermission !== 'granted' && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-xl flex items-start gap-3">
                  <div className="bg-yellow-100 p-2 rounded-full text-yellow-600 mt-0.5"><FiBell className="text-lg" /></div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-yellow-800">تنبيه هام</h3>
                    <p className="text-xs text-yellow-700 mb-2">الإشعارات غير مفعلة، قد تفوتك طلبات جديدة.</p>
                    <button onClick={() => Notification.requestPermission().then(p => { setNotificationPermission(p); p === 'granted' ? toast.success('تم التفعيل') : toast.error('تم الرفض'); })}
                      className="bg-yellow-600 text-white text-xs px-3 py-1.5 rounded-lg font-bold hover:bg-yellow-700 transition w-full">
                      تفعيل الإشعارات الآن
                    </button>
                  </div>
                </div>
              )}

              {/* إعدادات طرق الدفع - ديسكتوب */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1">
                    <span className="bg-blue-100 text-blue-600 p-1 rounded"><FiCreditCard className="text-xs" /></span>
                    طرق الدفع
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => updatePaymentMethods('cash')}
                    disabled={updatingPaymentMethods}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-sm font-bold transition-all ${
                      provider?.acceptsCash && !provider?.acceptsWallet
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span>💵</span>
                    <span>كاش</span>
                  </button>
                  <button
                    onClick={() => updatePaymentMethods('wallet')}
                    disabled={updatingPaymentMethods}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-sm font-bold transition-all ${
                      provider?.acceptsWallet && !provider?.acceptsCash
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span>👛</span>
                    <span>محفظة</span>
                  </button>
                  <button
                    onClick={() => updatePaymentMethods('both')}
                    disabled={updatingPaymentMethods}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-sm font-bold transition-all ${
                      provider?.acceptsWallet && provider?.acceptsCash
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span>💵👛</span>
                    <span>الكل</span>
                  </button>
                </div>
                <p className="text-[9px] text-gray-400 mt-1.5 text-center">اختر طريقة دفع واحدة أو كليهما</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide">حالة التوفر</h3>
                    <p className={`text-sm font-bold mt-1 ${provider?.available ? 'text-green-600' : 'text-red-600'}`}>
                      {provider?.available ? 'متاح لاستقبال الطلبات' : 'غير متاح حالياً'}
                    </p>
                  </div>
                  <button onClick={() => updateAvailability(!provider?.available)}
                    className={`relative inline-flex h-7 items-center rounded-full transition-colors ${provider?.available ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: '50px' }}>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${provider?.available ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center">
                  <div className="bg-yellow-50 p-2 rounded-full mb-2"><FiStar className="text-yellow-500 text-2xl" /></div>
                  <h3 className="text-2xl font-extrabold text-gray-800">{getFormattedRating(provider?.averageRating)}</h3>
                  <p className="text-xs text-gray-400 font-medium">متوسط التقييم</p>
                </div>
                <button onClick={navigateToGame} className="bg-purple-50 p-4 rounded-xl border border-purple-100 flex flex-col items-center justify-center text-center hover:bg-purple-100 transition group">
                  <div className="bg-purple-100 p-2 rounded-full mb-2 group-hover:scale-110 transition-transform"><FiZap className="text-purple-600 text-2xl" /></div>
                  <span className="text-sm font-bold text-purple-800">الالعاب</span>
                </button>
              </div>
            </div>
            <div className="flex-1 flex flex-col min-h-0">
              {confirmationRequests.length > 0 && (
                <div className="bg-blue-600 text-white p-3 rounded-xl shadow-lg flex-shrink-0 flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-full"><FiCheck className="text-xl" /></div>
                    <div>
                      <h3 className="font-bold text-sm">تأكيد إنهاء الخدمة</h3>
                      <p className="text-blue-100 text-xs">العميل: <span className="font-bold text-white">{confirmationRequests[0].client?.fullName}</span> | {confirmationRequests[0].service?.name}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => { setSelectedConfirmation(confirmationRequests[0]); setShowConfirmationDialog(true); }} className="bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-100 transition shadow-sm">تمت الخدمة</button>
                    <button onClick={() => handleConfirmation(confirmationRequests[0].id, false)} className="bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-800 transition border border-blue-500">لم تتم</button>
                  </div>
                </div>
              )}
              {/* الصف العلوي: الملخص المالي + طرق الدفع + التقييم */}
              <div className="flex gap-3 flex-shrink-0 mb-3">
                {/* الملخص المالي */}
                <div className="flex-1 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xs font-bold text-gray-500">الملخص المالي (الشهر)</h3>
                    {currentMonthPayment && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${currentMonthPayment.payment_status === 'paid' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-orange-50 text-orange-700 border-orange-100'}`}>
                        {currentMonthPayment.payment_status === 'paid' ? 'تم الدفع' : 'قيد الانتظار'}
                      </span>
                    )}
                  </div>
                  {currentMonthPayment ? (
                    <div className="space-y-1">
                      <div className="flex justify-between items-end">
                        <span className="text-[10px] text-gray-400">صافي الأرباح</span>
                        <span className="text-xl font-extrabold text-green-600">{formatCurrency(currentMonthPayment.netEarnings || currentMonthPayment.net_earnings || 0)}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400">الإجمالي</span>
                        <span className="font-medium text-gray-700">{formatCurrency(currentMonthPayment.totalEarnings || currentMonthPayment.total_earnings || 0)}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400">العمولة</span>
                        <span className="font-medium text-red-500">-{formatCurrency(currentMonthPayment.commission || 0)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 py-2 text-gray-400">
                      <FiDollarSign className="text-lg opacity-30" />
                      <span className="text-xs">لا توجد أرباح هذا الشهر</span>
                    </div>
                  )}
                </div>
                {/* طرق الدفع */}
                <div className="w-48 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 mb-2">طرق الدفع</h3>
                  <div className="flex gap-1.5">
                    <button onClick={() => updatePaymentMethods('cash')} disabled={updatingPaymentMethods}
                      className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-bold transition-all ${provider?.acceptsCash && !provider?.acceptsWallet ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      <span>💵</span><span>كاش</span>
                    </button>
                    <button onClick={() => updatePaymentMethods('wallet')} disabled={updatingPaymentMethods}
                      className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-bold transition-all ${provider?.acceptsWallet && !provider?.acceptsCash ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      <span>👛</span><span>محفظة</span>
                    </button>
                    <button onClick={() => updatePaymentMethods('both')} disabled={updatingPaymentMethods}
                      className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border text-[10px] font-bold transition-all ${provider?.acceptsWallet && provider?.acceptsCash ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      <span>💵👛</span><span>الكل</span>
                    </button>
                  </div>
                </div>
                {/* التقييم وحالة التوفر */}
                <div className="w-40 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-yellow-50 p-1.5 rounded-full"><FiStar className="text-yellow-500 text-sm" /></div>
                    <div>
                      <span className="text-lg font-extrabold text-gray-800">{getFormattedRating(provider?.averageRating)}</span>
                      <p className="text-[9px] text-gray-400">متوسط التقييم</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold ${provider?.available ? 'text-green-600' : 'text-red-600'}`}>
                      {provider?.available ? 'متاح' : 'مشغول'}
                    </span>
                    <button onClick={() => updateAvailability(!provider?.available)}
                      className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${provider?.available ? 'bg-green-500' : 'bg-red-500'}`}>
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform ${provider?.available ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>
              {/* الطلبات الواردة */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
                  <h3 className="text-base font-bold text-gray-800">الطلبات الواردة</h3>
                  <button onClick={() => { setActiveTab(1); setRequestSubTab(0); }} className="text-xs text-blue-600 font-bold hover:underline">
                    عرض الكل ({requests.filter(r => r.status === 'pending').length})
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {requests.filter(r => r.status === 'pending').length === 0 ? (
                      <div className="col-span-full h-full flex flex-col items-center justify-center text-gray-400 py-8">
                        <div className="bg-gray-50 p-3 rounded-full mb-2"><FiInbox className="text-3xl" /></div>
                        <p className="text-base font-bold text-gray-500">لا توجد طلبات جديدة</p>
                        <p className="text-xs text-gray-400">ستظهر الطلبات هنا فور وصولها</p>
                      </div>
                    ) : requests.filter(r => r.status === 'pending').map((request) => (
                      <div key={request.id} className="border border-gray-100 rounded-xl p-3 hover:shadow-md hover:border-blue-100 transition-all bg-white flex gap-3">
                        <img src={request.client?.avatarUrl || 'https://via.placeholder.com/150'} alt="Client" className="w-11 h-11 rounded-full bg-gray-100 flex-shrink-0 border border-gray-200" />
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div>
                            <p className="text-sm font-bold text-gray-900 truncate">{request.client?.fullName}</p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">{request.service?.name}</p>
                            <div className="mt-0.5">
                              {request.paymentMethod === 'wallet' ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
                                  💰 محفظة
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                                  💵 كاش
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => updateRequestStatus(request.id, 'accepted')} className="flex-1 bg-green-600 text-white py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 transition shadow-sm">قبول</button>
                            <button onClick={() => updateRequestStatus(request.id, 'rejected')} className="flex-1 bg-red-50 text-red-600 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition border border-red-100">رفض</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },

    // ── Tab 1: Requests ──────────────────────────────────────────
    {
      id: 1,
      name: 'الطلبات',
      icon: FiList,
      content: (
        <div className="p-4 md:p-5">
          <div className="flex justify-between items-center mb-4 md:mb-5">
            <h2 className="text-xl md:text-xl font-bold">الطلبات</h2>
            <button onClick={loadProviderData} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
              <FiRefreshCw className="text-gray-600" />
            </button>
          </div>
          <div className="bg-white md:bg-gray-50 rounded-xl shadow-sm overflow-hidden md:border border-gray-200">
            <div className="flex border-b border-gray-200">
              {['معلق', 'مقبول', 'مرفوض', 'مكتمل'].map((status, index) => (
                <button key={status}
                  className={`flex-1 py-3 text-center font-medium transition-colors text-xs md:text-sm ${requestSubTab === index ? 'text-gray-900 border-b-2 border-gray-900 bg-white md:bg-gray-100' : 'text-gray-600 hover:text-gray-900'}`}
                  onClick={() => setRequestSubTab(index)}>
                  {status}
                </button>
              ))}
            </div>
            <div className="p-3 md:p-4">
              {loading ? (
                <div className="flex justify-center py-8 md:py-10">
                  <div className="animate-spin rounded-full h-8 w-8 md:h-10 md:w-10 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <AnimatePresence>
                  {requests.filter(request => {
                    const status = request.status?.toLowerCase().trim();
                    switch (requestSubTab) {
                      case 0: return status === 'pending';
                      case 1: return status === 'accepted' || status === 'pending_provider_confirmation';
                      case 2: return status === 'rejected';
                      case 3: return status === 'completed';
                      default: return false;
                    }
                  }).map((request) => (
                    <motion.div key={request.id}
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                      className="bg-gray-50 md:bg-white p-3 md:p-4 border-b border-gray-200 last:border-b-0 md:rounded-lg md:mb-3 md:shadow-sm">
                      <div className="flex items-center space-x-3 space-x-reverse mb-3">
                        <img src={request.client?.avatarUrl || 'https://via.placeholder.com/150'} alt="Client" className="w-10 h-10 md:w-12 md:h-12 rounded-full" />
                        <div className="flex-1">
                          <p className="font-medium text-sm md:text-base">{request.client?.fullName || 'عميل'}</p>
                          {['accepted', 'pending_provider_confirmation'].includes(request.status?.toLowerCase().trim()) && (
                            <div className="text-xs md:text-sm text-gray-500">
                              <p>
                                رقم الهاتف: {request.client?.phone ? (
                                  <a href={`tel:${request.client.phone}`} className="text-blue-600 font-bold underline hover:text-blue-800 dir-ltr inline-block">
                                    {request.client.phone}
                                  </a>
                                ) : 'لا يوجد رقم هاتف'}
                              </p>
                              <p className="text-gray-800 font-bold mt-0.5">العنوان: {request.client?.address || request.client?.stateName || request.address || 'غير محدد'}</p>
                            </div>
                          )}
                        </div>
                        <div className="px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium text-white" style={{ backgroundColor: getStatusColor(request.status) }}>
                          {getStatusText(request.status)}
                        </div>
                      </div>
                      <div className="bg-white md:bg-gray-50 p-2 md:p-3 rounded-lg mb-3">
                        <p className="font-medium mb-1 text-sm md:text-base">{request.service?.name || 'خدمة'}</p>
                        <p className="text-xs md:text-sm text-gray-500 mb-1">{request.service?.description}</p>
                        <p className="text-xs md:text-sm text-gray-500">السعر: {formatCurrency(request.service?.price || 0)}</p>
                        <div className="mt-2">
                          {request.paymentMethod === 'wallet' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              💰 محفظة
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                              💵 كاش
                            </span>
                          )}
                        </div>
                      </div>
                      {request.status === 'pending' && (
                        <div className="flex space-x-2 space-x-reverse">
                          <button onClick={() => updateRequestStatus(request.id, 'accepted')} className="flex-1 bg-green-600 text-white py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm hover:bg-green-700 transition-colors flex items-center justify-center">
                            <FiCheck className="ml-1" />قبول
                          </button>
                          <button onClick={() => updateRequestStatus(request.id, 'rejected')} className="flex-1 bg-red-600 text-white py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm hover:bg-red-700 transition-colors flex items-center justify-center">
                            <FiX className="ml-1" />رفض
                          </button>
                        </div>
                      )}
                      {['accepted', 'pending_provider_confirmation'].includes(request.status) && (
                        <div className="flex flex-col gap-2">
                          <div className="flex space-x-2 space-x-reverse">
                            <button
                              onClick={() => navigateToChat(request)}
                              className="flex-1 bg-gray-900 text-white py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm hover:bg-gray-800 transition-colors flex items-center justify-center"
                            >
                              دردشة الآن
                            </button>
                            {request.status === 'pending_provider_confirmation' && (
                              <button
                                onClick={() => { setSelectedConfirmation(request); setShowConfirmationDialog(true); }}
                                className="flex-1 bg-green-600 text-white py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm hover:bg-green-700 transition-colors flex items-center justify-center"
                              >
                                تمت الخدمة
                              </button>
                            )}
                          </div>
                          <button
                            onClick={() => handleUnconfirmedServiceReport(request)}
                            className="w-full bg-red-600 text-white py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-1 shadow-sm"
                          >
                            الزبون لم ياكد خدمته
                          </button>
                        </div>
                      )}
                      {request.status?.toLowerCase().trim() === 'completed' && (
                        <div className="flex space-x-2 space-x-reverse">
                          <button
                            onClick={() => openComplaintDialog(request)}
                            className="flex-1 bg-red-50 border border-red-200 text-red-600 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm hover:bg-red-100 transition-colors flex items-center justify-center"
                          >
                            تقديم شكوى
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>
      ),
    },

    // ── Tab 2: Services ──────────────────────────────────────────
    // ✅ إصلاح: ServicesTab component مستقل لضمان re-render صحيح
    {
      id: 2,
      name: isDigital ? 'منتجاتي الرقمية' : isStore ? 'منتجاتي' : 'خدماتي',
      icon: FiTool,
      content: (
        <ServicesTab
          services={services}
          isDigital={isDigital}
          isStore={isStore}
          canEdit={canEdit}
          canDelete={canDelete}
          openAddServiceDialog={openAddServiceDialog}
          editService={editService}
          deleteService={deleteService}
          onShowQr={(s) => {
            setSelectedQrService(s);
            setQrModalOpen(true);
          }}
        />
      ),
    },

    // ── Tab 3: Notifications ─────────────────────────────────────
    {
      id: 3,
      name: 'الإشعارات',
      icon: FiBell,
      content: (
        <NotificationsScreen userType="provider" />
      ),
    },
  ];

  // ════════════════════════════════════════════════════════════════
  //  LOADING / ERROR STATES
  // ════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-100">
        <div className="text-center">
          <div className="bg-red-100 rounded-full p-4 md:p-6 mb-4 inline-block">
            <FiX className="text-3xl md:text-5xl text-red-600" />
          </div>
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">حدث خطأ غير متوقع</h2>
          <p className="text-xs md:text-sm text-gray-500 mb-6">يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى</p>
          <div className="flex space-x-3 space-x-reverse justify-center">
            <button onClick={loadProviderData} className="bg-gray-900 text-white py-2 px-4 md:py-3 md:px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors text-sm md:text-base">إعادة المحاولة</button>
            <button onClick={() => window.location.reload()} className="bg-blue-600 text-white py-2 px-4 md:py-3 md:px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm md:text-base">تحديث الصفحة</button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <>
      {/* ── Mobile ── */}
      <div className="md:hidden h-screen w-full bg-gray-100 flex items-center justify-center overflow-hidden">
        <div className="max-w-md w-full h-full bg-white flex flex-col relative shadow-2xl">
          <div className="flex-1 overflow-y-auto pb-20 scroll-smooth">
            {tabs[activeTab]?.content}
          </div>
          <div className="bg-white border-t border-gray-900 w-full shrink-0 h-16 fixed bottom-0 left-0 right-0 z-50">
            <div className="flex h-full">
              <button className={`flex-1 flex flex-col items-center justify-center transition-colors ${activeTab === 0 ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveTab(0)}>
                <FiHome className="text-xl mb-1" /><span className="text-xs">الرئيسية</span>
              </button>
              <button className={`flex-1 flex flex-col items-center justify-center transition-colors relative ${activeTab === 1 ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveTab(1)}>
                <FiList className="text-xl mb-1" /><span className="text-xs">الطلبات</span>
                <NotificationBadge count={unreadCount} />
              </button>
              <button className={`flex-1 flex flex-col items-center justify-center transition-colors relative ${activeTab === 2 ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveTab(2)}>
                <FiTool className="text-xl mb-1" /><span className="text-xs">{isDigital ? 'منتجاتي' : 'خدماتي'}</span>
              </button>
              <button className={`flex-1 flex flex-col items-center justify-center transition-colors relative ${activeTab === 3 ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveTab(3)}>
                <FiBell className="text-xl mb-1" /><span className="text-xs">الإشعارات</span>
                <NotificationBadge count={unreadNotificationsCount} />
              </button>
              <Link to="/provider/conversations" className="flex-1 flex flex-col items-center justify-center transition-colors text-gray-600 hover:text-gray-900">
                <FiMessageSquare className="text-xl mb-1" /><span className="text-xs">الدردشة</span>
              </Link>
              <Link to="/provider/profile" className="flex-1 flex flex-col items-center justify-center transition-colors text-gray-600 hover:text-gray-900">
                <FiUser className="text-xl mb-1" /><span className="text-xs">الملف الشخصي</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Desktop ── */}
      <div className="hidden md:flex h-screen bg-gray-50 overflow-hidden">
        <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col h-full`}>
          <div className="p-3 border-b border-gray-700 flex-shrink-0">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="w-full flex items-center justify-center md:justify-start space-x-2 space-x-reverse hover:bg-gray-800 p-2 rounded-lg transition-colors">
              <FiMenu className="text-xl" />
              {sidebarOpen && <span className="font-medium">القائمة</span>}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800">
            {[
              { id: 0, label: 'الرئيسية', icon: FiHome },
              { id: 1, label: 'الطلبات', icon: FiList, badge: unreadCount },
              { id: 2, label: isDigital ? 'منتجاتي الرقمية' : isStore ? 'منتجاتي' : 'خدماتي', icon: FiTool },
              { id: 3, label: 'الإشعارات', icon: FiBell, badge: unreadNotificationsCount },
            ].map(({ id, label, icon: Icon, badge }) => (
              <button key={id} onClick={() => setActiveTab(id)}
                className={`w-full flex items-center space-x-3 space-x-reverse p-2.5 rounded-lg mb-1 transition-colors relative ${activeTab === id ? 'bg-gray-800 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <Icon className="text-lg flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="text-sm font-medium">{label}</span>
                    {badge > 0 && (
                      <span className="mr-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </>
                )}
                {!sidebarOpen && badge > 0 && <NotificationBadge count={badge} className="top-1 right-1" />}
              </button>
            ))}
            <Link to="/provider/conversations" className="w-full flex items-center space-x-3 space-x-reverse p-2.5 rounded-lg mb-1 transition-colors text-gray-300 hover:bg-gray-800">
              <FiMessageSquare className="text-lg flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">الدردشة</span>}
            </Link>
            <Link to="/provider/profile" className="w-full flex items-center space-x-3 space-x-reverse p-2.5 rounded-lg mb-1 transition-colors text-gray-300 hover:bg-gray-800">
              <FiUser className="text-lg flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">الملف الشخصي</span>}
            </Link>
          </div>

          {sidebarOpen && (
            <div className="p-3 border-t border-gray-700 flex-shrink-0 space-y-2">
              <div className="flex items-center space-x-3 space-x-reverse">
                <img src={provider?.avatarUrl || 'https://via.placeholder.com/150'} alt="Profile" className="w-9 h-9 rounded-full" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{provider?.fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{provider?.email}</p>
                </div>
              </div>
              <button onClick={() => navigate('/payment-method')} className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm">
                <FiUpload className="text-base" /><span>الدفع</span>
              </button>
              <Link to="/contact-us" className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm">
                <FiHeadphones className="text-base" /><span>التواصل معنا</span>
              </Link>
              <Link to="/rules-and-terms" className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm">
                <FiBookOpen className="text-base" /><span>القواعد والقوانين</span>
              </Link>
              <button onClick={handleSignOut} className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-red-700 bg-red-600 transition-colors text-sm">
                <FiLogOut className="text-base" /><span>تسجيل الخروج</span>
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col overflow-hidden h-full">
          <div className="bg-white border-b border-gray-200 p-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-2 space-x-reverse">
              <h1 className="text-xl font-bold text-gray-900">{tabs[activeTab]?.name}</h1>
              {/* أزرار المتاجر والبحث تظهر مباشرة في أعلى الشاشة */}
              <Link to="/providers" className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition text-xs font-bold text-gray-700 mr-4">
                <FiSearch className="text-sm" />
                <span>بحث</span>
              </Link>
              <Link to="/store" className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 hover:bg-orange-100 border border-orange-200 transition text-xs font-bold text-orange-700">
                <FiShoppingCart className="text-sm" />
                <span>المتجر</span>
              </Link>
            </div>
            <div className="flex items-center space-x-3 space-x-reverse">
              <button onClick={loadProviderData} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <FiRefreshCw className="text-gray-600" />
              </button>
              {activeTab === 2 && (
                <button onClick={openAddServiceDialog} className="bg-gray-900 text-white px-3 py-2 rounded-lg flex items-center space-x-2 space-x-reverse hover:bg-gray-800 transition-colors text-sm">
                  <FiPlus />
                  <span>{isDigital ? 'رفع منتج' : isStore ? 'إضافة منتج' : 'إضافة خدمة'}</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-6xl mx-auto h-full">
              {tabs[activeTab]?.content}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          SERVICE / PRODUCT DIALOG
      ══════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showServiceDialog && (
          <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[94vh] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {editingService
                      ? (isDigital ? 'تعديل المنتج الرقمي' : isStore ? 'تعديل المنتج' : 'تعديل الخدمة')
                      : (isDigital ? 'رفع منتج رقمي جديد' : isStore ? 'إضافة منتج جديد' : 'إضافة خدمة جديدة')}
                  </h3>
                  {isDigital && (
                    <p className="text-[11px] text-violet-600 font-medium mt-0.5">
                      {serviceFormData.productType === 'pdf' ? 'ملف PDF'
                        : serviceFormData.productType === 'zip' ? 'ملف ZIP / مشروع'
                        : 'رابط خارجي'}
                    </p>
                  )}
                </div>
                <button onClick={() => { setShowServiceDialog(false); resetServiceForm(); }} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
                  <FiX className="text-gray-500 text-lg" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

                {/* Cover Image */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                    صورة الغلاف <span className="text-gray-400 font-normal">(اختياري)</span>
                  </label>
                  <div
                    onClick={() => document.getElementById('svc-img-input').click()}
                    className={`relative border-2 border-dashed rounded-xl overflow-hidden cursor-pointer transition-colors
                      ${serviceImagePreview ? 'border-transparent h-44' : 'border-gray-200 hover:border-gray-400 h-20'}`}
                  >
                    {serviceImagePreview ? (
                      <>
                        <img src={serviceImagePreview} alt="" className="w-full h-full object-cover" />
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setServiceImagePreview(null); setServiceImageFile(null); }}
                          className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600">
                          <FiX size={11} />
                        </button>
                        <div className="absolute inset-x-0 bottom-0 bg-black/30 text-white text-xs text-center py-1.5">اضغط لتغيير الصورة</div>
                      </>
                    ) : (
                      <div className="h-full flex items-center justify-center gap-2 text-gray-400">
                        <FiCamera size={18} /><span className="text-xs font-medium">اضغط لإضافة صورة الغلاف</span>
                      </div>
                    )}
                  </div>
                  <input id="svc-img-input" type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files[0]; if (f) { setServiceImageFile(f); setServiceImagePreview(URL.createObjectURL(f)); } }}
                  />
                </div>

                {/* Media Gallery (multiple images + videos) */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                    معرض الوسائط (صور وفيديوهات) <span className="text-gray-400 font-normal">(اختياري)</span>
                  </label>
                  <div
                    onClick={() => document.getElementById('svc-media-input').click()}
                    className="border-2 border-dashed border-gray-200 hover:border-blue-300 rounded-xl p-4 cursor-pointer transition-colors text-center"
                  >
                    <div className="flex items-center justify-center gap-2 text-gray-400">
                      <FiCamera size={16} />
                      <span className="text-xs">اضغط لاختيار عدة صور وفيديوهات</span>
                    </div>
                    <p className="text-[10px] text-gray-300 mt-1">الحد الأقصى للفيديو: 10 ميجابايت</p>
                  </div>
                  <input
                    id="svc-media-input"
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/ogg,video/quicktime"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const valid = [];
                      for (const f of files) {
                        const isVideo = f.type.startsWith('video/');
                        if (isVideo && f.size > 10 * 1024 * 1024) {
                          toast.error(`الفيديو "${f.name}" يتجاوز 10 ميجابايت`);
                          continue;
                        }
                        valid.push({ file: f, previewUrl: URL.createObjectURL(f) });
                      }
                      if (valid.length) {
                        setServiceMediaFiles(prev => [...prev, ...valid]);
                      }
                      e.target.value = '';
                    }}
                  />

                  {/* Selected media previews */}
                  {(serviceMediaFiles.length > 0 || serviceFormData.mediaUrls.length > 0) && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {/* Existing URLs (when editing) */}
                      {serviceFormData.mediaUrls.map((url, i) => (
                        <div key={`url-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                          {url.toLowerCase().includes('.mp4') || url.toLowerCase().includes('.webm') || url.toLowerCase().includes('.ogg') ? (
                            <video src={url} className="w-full h-full object-cover" muted playsInline />
                          ) : (
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setServiceFormData(p => ({
                                ...p,
                                mediaUrls: p.mediaUrls.filter((_, idx) => idx !== i),
                              }));
                            }}
                            className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-0.5 shadow hover:bg-red-600"
                          >
                            <FiX size={10} />
                          </button>
                        </div>
                      ))}

                      {/* Newly selected files */}
                      {serviceMediaFiles.map((item, i) => (
                        <div key={`file-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                          {item.file.type.startsWith('video/') ? (
                            <video src={item.previewUrl} className="w-full h-full object-cover" muted playsInline />
                          ) : (
                            <img src={item.previewUrl} alt="" className="w-full h-full object-cover" />
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setServiceMediaFiles(prev => prev.filter((_, idx) => idx !== i));
                            }}
                            className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-0.5 shadow hover:bg-red-600"
                          >
                            <FiX size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Digital: Product Type Selector */}
                {isDigital && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2 text-right">نوع المنتج الرقمي</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'pdf',  label: 'PDF',         Icon: FiFile,    active: 'border-violet-400 bg-violet-50 text-violet-700' },
                        { value: 'zip',  label: 'ZIP / مشروع', Icon: FiPackage, active: 'border-emerald-400 bg-emerald-50 text-emerald-700' },
                        { value: 'link', label: 'رابط خارجي',  Icon: FiLink,    active: 'border-amber-400 bg-amber-50 text-amber-700' },
                      ].map(({ value, label, Icon, active }) => (
                        <button key={value} type="button"
                          onClick={() => setServiceFormData(p => ({ ...p, productType: value }))}
                          className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-xs font-bold transition
                            ${serviceFormData.productType === value ? active : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200'}`}>
                          <Icon size={18} />{label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                    {isDigital ? 'اسم المنتج' : isStore ? 'اسم المنتج' : 'اسم الخدمة'}
                  </label>
                  <input type="text"
                    className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 text-right"
                    value={serviceFormData.name}
                    onChange={(e) => setServiceFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder={isDigital ? 'مثال: قالب CV احترافي' : isStore ? 'مثال: حقيبة جلد' : 'مثال: صيانة كهربائية'}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">الوصف</label>
                  <textarea
                    className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 text-right resize-none"
                    value={serviceFormData.description}
                    onChange={(e) => setServiceFormData(p => ({ ...p, description: e.target.value }))}
                    rows="3" placeholder="وصف تفصيلي..."
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">السعر (د.ج)</label>
                  <input type="number" min="0"
                    disabled={editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq)}
                    className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 text-right disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    value={serviceFormData.price}
                    onChange={(e) => setServiceFormData(p => ({ ...p, price: e.target.value }))}
                    placeholder="0"
                  />
                  {editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq) && (
                    <p className="text-[11px] text-amber-600 mt-1 text-right">
                      لا يمكن تعديل السعر لوجود عمليات شراء أو طلبات مسجلة على هذا المنتج/الخدمة.
                    </p>
                  )}
                </div>

                {/* Store: Stock + Category */}
                {isStore && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">الكمية في المخزون</label>
                      <input type="number" min="0"
                        className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 text-right"
                        value={serviceFormData.stock}
                        onChange={(e) => setServiceFormData(p => ({ ...p, stock: e.target.value }))}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">التصنيف</label>
                      <input type="text"
                        className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 text-right"
                        value={serviceFormData.categoryName}
                        onChange={(e) => setServiceFormData(p => ({ ...p, categoryName: e.target.value }))}
                        placeholder="مثال: ملابس، إلكترونيات"
                      />
                    </div>
                  </div>
                )}

                {/* Digital: File or Link */}
                {isDigital && (
                  <>
                    {serviceFormData.productType === 'link' ? (
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                          رابط المنتج <span className="text-red-500">*</span>
                        </label>
                        <input type="url"
                          disabled={editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq)}
                          className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 text-left disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                          value={serviceFormData.linkUrl}
                          onChange={(e) => setServiceFormData(p => ({ ...p, linkUrl: e.target.value }))}
                          placeholder="https://..." dir="ltr"
                        />
                        {editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq) && (
                          <p className="text-[11px] text-amber-600 mt-1 text-right">
                            لا يمكن تعديل الرابط لوجود عمليات شراء مرتبطة بهذا المنتج.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                          الملف الرسمي ({serviceFormData.productType.toUpperCase()}) <span className="text-red-500">*</span>
                        </label>
                        <div
                          onClick={() => {
                            if (editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq)) {
                              toast.error('لا يمكن تغيير الملف الرسمي لوجود عمليات شراء مسجلة على هذا المنتج');
                              return;
                            }
                            document.getElementById('svc-file-input').click();
                          }}
                          className={`border-2 border-dashed rounded-xl p-4 transition-colors text-center
                            ${editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq)
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                              : 'border-gray-200 hover:border-violet-300 cursor-pointer'}`}
                        >
                          {editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq) ? (
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                              <FiLock size={16} />
                              <span className="text-xs font-medium">الملف مقفل بسبب وجود عمليات شراء</span>
                            </div>
                          ) : serviceFile ? (
                            <div className="flex items-center justify-center gap-2 text-violet-700">
                              <FiCheck size={16} />
                              <span className="text-xs font-bold truncate max-w-[200px]">{serviceFile.name}</span>
                            </div>
                          ) : serviceFormData.fileUrl ? (
                            <div className="flex items-center justify-center gap-2 text-violet-600">
                              <FiFile size={16} />
                              <span className="text-xs font-medium">ملف مرفوع مسبقاً — اضغط لتغييره</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                              <FiUpload size={16} />
                              <span className="text-xs">اضغط لرفع الملف</span>
                            </div>
                          )}
                        </div>
                        <input id="svc-file-input" type="file"
                          accept={serviceFormData.productType === 'pdf' ? '.pdf' : '.zip,.rar,.7z'}
                          className="hidden"
                          onChange={(e) => { const f = e.target.files[0]; if (f) setServiceFile(f); }}
                        />
                      </div>
                    )}

                    {/* Preview file */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                        ملف المعاينة / صفحات مجانية <span className="text-gray-400 font-normal">(اختياري)</span>
                      </label>
                      <div
                        onClick={() => {
                          if (editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq)) {
                            toast.error('لا يمكن تغيير ملف المعاينة لوجود عمليات شراء مسجلة على هذا المنتج');
                            return;
                          }
                          document.getElementById('svc-prev-input').click();
                        }}
                        className={`border-2 border-dashed rounded-xl p-4 transition-colors text-center
                          ${editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq)
                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                            : 'border-gray-200 hover:border-blue-300 cursor-pointer'}`}
                      >
                        {editingService && (serviceStatuses[editingService.id]?.hasPurchase || serviceStatuses[editingService.id]?.hasAnyReq) ? (
                          <div className="flex items-center justify-center gap-2 text-gray-400">
                            <FiLock size={16} />
                            <span className="text-xs font-medium">المعاينة مقفلة بسبب وجود عمليات شراء</span>
                          </div>
                        ) : previewFile ? (
                          <div className="flex items-center justify-center gap-2 text-blue-600">
                            <FiCheck size={16} />
                            <span className="text-xs font-bold truncate max-w-[200px]">{previewFile.name}</span>
                          </div>
                        ) : serviceFormData.previewUrl ? (
                          <div className="flex items-center justify-center gap-2 text-blue-500">
                            <FiEye size={16} />
                            <span className="text-xs font-medium">معاينة مرفوعة — اضغط لتغييرها</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-gray-400">
                            <FiEye size={16} />
                            <span className="text-xs">ارفع ملف معاينة للعملاء (PDF أو صورة)</span>
                          </div>
                        )}
                      </div>
                      <input id="svc-prev-input" type="file" accept=".pdf,image/*" className="hidden"
                        onChange={(e) => { const f = e.target.files[0]; if (f) setPreviewFile(f); }}
                      />
                    </div>

                    {/* Allow Download Toggle */}
                    {serviceFormData.productType !== 'link' && (
                      <div>
                        <div className={`flex items-center justify-between p-3.5 rounded-xl border transition-colors
                          ${serviceFormData.allowDownload ? 'border-emerald-200 bg-emerald-50/60' : 'border-gray-200 bg-gray-50/60'}`}>
                          <div className="flex items-center gap-2.5">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors
                              ${serviceFormData.allowDownload ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                              <FiDownload size={16} />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-800">السماح بالتحميل</p>
                              <p className="text-[10px] text-gray-400">
                                {serviceFormData.allowDownload
                                  ? 'سيظهر زر تحميل للعملاء في صفحة مكتبتي مع علامة مائية (Watermark)'
                                  : 'العملاء يستطيعون القراءة/المعاينة فقط بدون تحميل'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setServiceFormData(p => ({ ...p, allowDownload: !p.allowDownload }))}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${serviceFormData.allowDownload ? 'bg-emerald-500' : 'bg-gray-300'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${serviceFormData.allowDownload ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Info notice */}
                {(isStore || isDigital) && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex gap-2">
                    <FiInfo size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      {isDigital
                        ? 'بعد إضافة المنتج، إذا تمت عمليات شراء عليه، لن تتمكن من تغيير الملف الرسمي أو ملف المعاينة أو السعر.'
                        : 'إذا تم إنشاء طلبات على هذا المنتج، ستتمكن من تعديله باستثناء السعر.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-2.5 px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <button
                  onClick={() => { setShowServiceDialog(false); resetServiceForm(); }}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={saveService}
                  disabled={uploadingServiceImage || uploadingFile || uploadingPreview || uploadingMedia}
                  className="flex-1 bg-gray-900 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {(uploadingServiceImage || uploadingFile || uploadingPreview || uploadingMedia) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      جاري الرفع...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      {editingService ? 'حفظ التعديلات' : (isDigital ? 'رفع المنتج' : 'إضافة')}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Confirmation Dialog ── */}
      <AnimatePresence>
        {showConfirmationDialog && selectedConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl"
            >
              <div className="text-center mb-6">
                <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
                  <FiCheck className="text-green-600 text-2xl mx-auto" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">تأكيد إتمام الخدمة</h3>
                <p className="text-gray-600">
                  هل أنت متأكد من إتمام الخدمة للعميل{' '}
                  <span className="font-bold">{selectedConfirmation.client?.fullName}</span>؟
                </p>
                <p className="text-red-600 font-bold mt-3 text-sm bg-red-50 p-2.5 rounded-lg border border-red-100">
                  ⚠️ تأكد جيداً من إتمام العمل واستلام كامل مستحقاتك قبل التأكيد!
                </p>
                <p className="text-xs text-gray-500 mt-3">سيتم حذف المحادثة وإضافة الخدمة لأرباحك الشهرية</p>
              </div>
              <div className="flex space-x-3 space-x-reverse">
                <button onClick={() => { setShowConfirmationDialog(false); setSelectedConfirmation(null); }}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors">
                  إلغاء
                </button>
                <button onClick={() => handleConfirmation(selectedConfirmation.id, true)}
                  className="flex-1 bg-green-600 text-white py-3 rounded-xl font-medium hover:bg-green-700 transition-colors">
                  تأكيد الإتمام
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      {/* Support Floating Button */}
      <button
        onClick={handleSupportClick}
        className="fixed bottom-20 left-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all z-40 flex items-center justify-center"
        title="الدعم الفني"
      >
        <FiHeadphones className="w-5.5 h-5.5" />
      </button>

      {/* ═══════════════ Modal: تقديم شكوى ═══════════════ */}
      {complaintModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-lg rounded-3xl p-6 shadow-2xl animate-fade-in" dir="rtl">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
                  <FiAlertTriangle size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 font-sans">تقديم شكوى</h2>
                  <p className="text-xs text-gray-500 font-sans">يرجى كتابة تفاصيل المشكلة</p>
                </div>
              </div>
              <button 
                onClick={() => setComplaintModalOpen(false)} 
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-start gap-3 text-red-800 text-sm">
                <FiAlertCircle className="text-red-500 mt-0.5 shrink-0" size={18} />
                <div>
                  <p className="font-bold font-sans">تنبيه هام</p>
                  <p className="mt-0.5 font-sans">إذا قمت بتقديم شكوى كاذبة قد يتم تجميد حسابك.</p>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600 mb-1.5 block font-bold font-sans">سبب الشكوى</label>
                <textarea
                  value={complaintReason}
                  onChange={(e) => setComplaintReason(e.target.value)}
                  placeholder="اكتب هنا تفاصيل المشكلة أو السبب الذي يجعلك تقدم هذه الشكوى..."
                  rows={4}
                  className="w-full p-4 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none font-sans"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSendComplaint}
                  disabled={submittingComplaint}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl font-bold text-sm transition font-sans"
                >
                  {submittingComplaint ? 'جاري الإرسال...' : 'تأكيد وإرسال الشكوى'}
                </button>
                <button
                  onClick={() => setComplaintModalOpen(false)}
                  disabled={submittingComplaint}
                  className="py-3 px-6 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-xl font-bold text-sm transition font-sans"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ServiceQrModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        service={selectedQrService}
      />

      <QrScannerModal
        isOpen={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        clientId={user?.id}
        clientWallet={provider?.wallet || 0}
        onPurchaseSuccess={() => {
          loadProviderData();
          setQrScannerOpen(false);
        }}
        onWalletCodeDetected={(walletCode) => {
          navigate(`/qr-transfer/${walletCode}`);
        }}
      />
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ServiceQrModal — نافذة منبثقة لتوليد وعرض كود QR للمنتج
// ═══════════════════════════════════════════════════════════════
const ServiceQrModal = ({ isOpen, onClose, service }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && service) {
      generateQr();
    }
  }, [isOpen, service]);

  const generateQr = async () => {
    setLoading(true);
    try {
      const dataText = service.qrData || `khidma_purchase:${service.id}`;
      const url = await QRCode.toDataURL(dataText, {
        width: 300,
        margin: 2,
        color: {
          dark: '#1e293b', // slate-800
          light: '#ffffff'
        }
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Failed to generate QR Code:', err);
      toast.error('فشل توليد رمز QR');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.download = `qr_${service.name}.png`;
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('تم تحميل كود QR بنجاح');
  };

  const printQRCode = () => {
    if (!qrCodeUrl) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>طباعة رمز QR - ${service.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              font-family: sans-serif;
              direction: rtl;
              color: #1e293b;
              margin: 0;
            }
            .container {
              text-align: center;
              border: 2px solid #e2e8f0;
              padding: 40px;
              border-radius: 24px;
              box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
            }
            img {
              width: 250px;
              height: 250px;
              margin-bottom: 20px;
            }
            h2 {
              margin: 0 0 8px 0;
              font-size: 20px;
              font-weight: 800;
            }
            p {
              margin: 0;
              font-size: 14px;
              color: #64748b;
            }
            .price {
              font-size: 18px;
              font-weight: 700;
              color: #2563eb;
              margin-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <img src="${qrCodeUrl}" />
            <h2>${service.name}</h2>
            <p>امسح الكود عبر تطبيق خدمة للشراء المباشر</p>
            <div class="price">${service.price?.toLocaleString('ar-DZ')} د.ج</div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 500);
            }
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  const shareQRCode = async () => {
    if (!qrCodeUrl) return;
    if (navigator.share) {
      try {
        const response = await fetch(qrCodeUrl);
        const blob = await response.blob();
        const file = new File([blob], `qr_${service.name}.png`, { type: 'image/png' });
        await navigator.share({
          files: [file],
          title: `كود QR لشراء: ${service.name}`,
          text: `قام التاجر بمشاركة رمز QR لشراء المنتج "${service.name}" بمبلغ ${service.price} د.ج. امسح الرمز في تطبيق خدمة لشراء المنتج مباشرة من محفظتك.`,
        });
        toast.success('تمت المشاركة بنجاح');
      } catch (err) {
        console.error('Error sharing QR Code:', err);
      }
    } else {
      downloadQRCode();
    }
  };

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" dir="rtl">
      <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-xl transition">
          <FiX className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center mt-4">
          <span className="text-xs bg-blue-50 text-blue-600 font-bold px-3 py-1 rounded-full mb-3">
            كود QR للمنتج
          </span>
          <h3 className="text-base font-bold text-gray-900 mb-1 truncate max-w-full px-2">{service.name}</h3>
          <p className="text-xs text-gray-400 mb-5">امسح الكود من تطبيق خدمة للشراء السريع من المحفظة</p>

          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-inner mb-6 relative w-56 h-56 flex items-center justify-center">
            {loading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            ) : (
              <img src={qrCodeUrl} alt="QR Code" className="w-full h-full object-contain" />
            )}
          </div>

          <div className="text-sm font-extrabold text-blue-600 mb-6 bg-blue-50/50 px-4 py-2 rounded-xl">
            السعر: {service.price?.toLocaleString('ar-DZ')} د.ج
          </div>

          <div className="grid grid-cols-3 gap-2.5 w-full">
            <button
              onClick={downloadQRCode}
              className="flex flex-col items-center justify-center gap-1.5 p-3 bg-gray-50 hover:bg-gray-100 rounded-2xl transition border border-gray-100 active:scale-95 text-gray-700"
            >
              <FiDownload className="w-5 h-5 text-blue-600" />
              <span className="text-[10px] font-bold">تحميل</span>
            </button>

            <button
              onClick={shareQRCode}
              className="flex flex-col items-center justify-center gap-1.5 p-3 bg-gray-50 hover:bg-gray-100 rounded-2xl transition border border-gray-100 active:scale-95 text-gray-700"
            >
              <FiShare2 className="w-5 h-5 text-green-600" />
              <span className="text-[10px] font-bold">مشاركة</span>
            </button>

            <button
              onClick={printQRCode}
              className="flex flex-col items-center justify-center gap-1.5 p-3 bg-gray-50 hover:bg-gray-100 rounded-2xl transition border border-gray-100 active:scale-95 text-gray-700"
            >
              <FiPrinter className="w-5 h-5 text-purple-600" />
              <span className="text-[10px] font-bold">طباعة</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderMainScreen;